import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { moment } from "@msuite/utils/date";
import type { Nullable, Tables } from "@msuite/utils/types";
import { client } from "@/hono";

/**
 * The tour waves an order delivered on `date` can be booked into. Every wave of the organisation
 * is read once, under the key the tour wave list reads too, and narrowed here to the waves running
 * on the date's weekday — so a new delivery date filters without a request and keeps a selected
 * wave that also runs on the new day, and the list's refetch after a create, edit or delete
 * refreshes the cache this reads.
 *
 * @param date - The delivery date; no date offers no wave.
 */
export function useTourWaves(date: Nullable<string>) {
	/** Query */
	const query = useQuery({
		queryKey: ["tour-waves"],
		queryFn: async () => {
			const response = await client.stock["tour-waves"].$get();
			const data = await responseHandler(response);
			return guardArray(data.tourWaves);
		},
	});

	/** Computed */
	const tourWaves = date ? getTourWavesOnWeekday(guardArray(query.data), date) : [];

	/** Return */
	return { ...query, tourWaves };
}

/**
 * Keep the waves that run on the weekday of `date`. Weekdays are `moment().weekday()` values,
 * Monday-first under every locale in `@msuite/utils/date`: 0 is Monday, 6 is Sunday.
 */
function getTourWavesOnWeekday(tourWaves: Array<Tables<"tourWaves">>, date: string) {
	const weekday = moment(date).weekday();
	const tourWavesOnWeekday: Array<Tables<"tourWaves">> = [];
	for (const tourWave of tourWaves) {
		if (tourWave.weekdays.includes(weekday)) tourWavesOnWeekday.push(tourWave);
	}
	return tourWavesOnWeekday;
}
